/**
 * Illustrated background for the "Perfect Gift" CTA: a length of triangle
 * bunting suspended over a wood-grain table strewn with wrapped, ribboned
 * gifts. Rendered as inline SVG so it inherits the new luxe palette
 * (graphite + stormy teal + yale blue + alabaster) and stays crisp at any
 * size. The hover-animated featured book sits in front of this scene; see
 * `.gift-scene` in index.css.
 */
export function GiftScene() {
  return (
    <svg
      className="gift-scene-bg"
      viewBox="0 0 600 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <defs>
        <linearGradient id="gift-room-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#eaf3f3" />
          <stop offset="0.55" stopColor="#f0f0f0" />
          <stop offset="1" stopColor="#d9d9d9" />
        </linearGradient>
        <linearGradient id="gift-table-surface" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#C9966A" />
          <stop offset="1" stopColor="#A0703A" />
        </linearGradient>
        <radialGradient id="gift-vignette" cx="0.5" cy="0.45" r="0.7">
          <stop offset="0.5" stopColor="rgba(255,255,255,0)" />
          <stop offset="1" stopColor="rgba(32,32,32,0.18)" />
        </radialGradient>
      </defs>

      {/* room */}
      <rect width="600" height="600" fill="url(#gift-room-bg)" />

      {/* warm floor behind the table */}
      <rect x="0" y="432" width="600" height="168" fill="#F2E4C8" />

      {/* table surface plank */}
      <rect x="-10" y="432" width="620" height="22" fill="url(#gift-table-surface)" />
      {/* tabletop top-edge highlight */}
      <rect x="0" y="430" width="600" height="3" fill="#D4A068" opacity="0.85" />
      {/* table apron (front face below surface) */}
      <rect x="0" y="454" width="600" height="18" fill="#7B4A1E" />
      {/* table legs */}
      <rect x="70" y="472" width="52" height="128" fill="#7B4A1E" rx="2" />
      <rect x="478" y="472" width="52" height="128" fill="#7B4A1E" rx="2" />

      {/* === wrapped gifts on the table === */}

      {/* back gift — tall, yale blue */}
      <g transform="translate(86, 320)">
        <rect width="120" height="120" fill="#284b63" rx="2" />
        <rect width="120" height="120" fill="url(#gift-vignette)" rx="2" />
        {/* vertical ribbon */}
        <rect x="54" y="0" width="12" height="120" fill="#a7ced1" />
        {/* horizontal ribbon */}
        <rect x="0" y="54" width="120" height="12" fill="#a7ced1" />
        {/* bow loops */}
        <ellipse cx="48" cy="-2" rx="14" ry="10" fill="#a7ced1" />
        <ellipse cx="72" cy="-2" rx="14" ry="10" fill="#a7ced1" />
        <circle cx="60" cy="-2" r="6" fill="#7bb6b9" />
      </g>

      {/* wide low gift — graphite */}
      <g transform="translate(220, 360)">
        <rect width="170" height="80" fill="#202020" rx="2" />
        <rect width="170" height="80" fill="url(#gift-vignette)" rx="2" />
        <rect x="78" y="0" width="14" height="80" fill="#3c6e71" />
        <rect x="0" y="34" width="170" height="12" fill="#3c6e71" />
        <ellipse cx="76" cy="-2" rx="14" ry="9" fill="#3c6e71" />
        <ellipse cx="94" cy="-2" rx="14" ry="9" fill="#3c6e71" />
        <circle cx="85" cy="-2" r="5" fill="#7bb6b9" />
      </g>

      {/* small front gift — stormy teal */}
      <g transform="translate(420, 350)">
        <rect width="100" height="90" fill="#3c6e71" rx="2" />
        <rect width="100" height="90" fill="url(#gift-vignette)" rx="2" />
        <rect x="44" y="0" width="12" height="90" fill="#f0f0f0" />
        <rect x="0" y="40" width="100" height="12" fill="#f0f0f0" />
        <ellipse cx="40" cy="-1" rx="12" ry="8" fill="#f0f0f0" />
        <ellipse cx="60" cy="-1" rx="12" ry="8" fill="#f0f0f0" />
        <circle cx="50" cy="-1" r="5" fill="#d9d9d9" />
      </g>

      {/* tiny tucked-behind gift — alabaster */}
      <g transform="translate(28, 380)">
        <rect width="64" height="60" fill="#d9d9d9" rx="2" />
        <rect x="28" y="0" width="8" height="60" fill="#284b63" />
        <rect x="0" y="26" width="64" height="8" fill="#284b63" />
      </g>
      <g transform="translate(508, 388)">
        <rect width="64" height="52" fill="#d9d9d9" rx="2" />
        <rect x="28" y="0" width="8" height="52" fill="#3c6e71" />
        <rect x="0" y="22" width="64" height="8" fill="#3c6e71" />
      </g>

      {/* soft floor vignette */}
      <rect width="600" height="600" fill="url(#gift-vignette)" opacity="0.5" />
    </svg>
  );
}

/**
 * The bunting itself — a separate SVG so it can be animated with its own
 * gentle sway under the parent .gift-scene:hover rule.
 */
export function GiftBunting() {
  const flagColors = ["#3c6e71", "#7bb6b9", "#284b63", "#3e7397", "#3c6e71", "#7bb6b9", "#284b63"];
  return (
    <svg
      className="gift-bunting"
      viewBox="0 0 600 90"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      <path
        d="M 30 8 Q 300 58 570 8"
        stroke="#202020"
        strokeWidth="1.4"
        fill="none"
      />
      {flagColors.map((color, i) => {
        const total = flagColors.length;
        const t = (i + 0.5) / total;
        // Quadratic bezier: P0=(30,8), P_ctrl=(300,58), P2=(570,8)
        const mt = 1 - t;
        const x = mt * mt * 30 + 2 * mt * t * 300 + t * t * 570;
        const y = mt * mt * 8  + 2 * mt * t * 58  + t * t * 8;
        // Tangent: dx/dt=540 (constant), dy/dt=100-200t
        const angle = Math.atan2(100 - 200 * t, 540) * 180 / Math.PI;
        return (
          <g key={i} transform={`translate(${x} ${y}) rotate(${angle})`}>
            <path d="M -14 0 L 14 0 L 0 32 Z" fill={color} />
            <path d="M -14 0 L 14 0 L 0 32 Z" fill="rgba(255,255,255,0.08)" />
          </g>
        );
      })}
    </svg>
  );
}
